export const TEMPLATE_SCREEN_TAGS = [
  "inbox",
  "quotation",
  "broadcast",
  "call_reply",
  "lead_status",
  "email_enquiry",
];

export const DRAFT_TAG = "draft";

export const TEMPLATE_APP_USAGE_OPTIONS = [
  { value: "draft", label: "Draft", hint: "Not shown in any live picker until you assign screens" },
  { value: "all", label: "All screens", hint: "Show in every live picker" },
  { value: "inbox", label: "Inbox", hint: "Greeting and simple replies in chat" },
  { value: "quotation", label: "Quotation", hint: "Quote, PDF, and price messages" },
  { value: "broadcast", label: "Broadcast", hint: "Campaigns and bulk sends" },
  { value: "call_reply", label: "Calls auto-reply", hint: "Incoming, ended, or missed-call templates" },
  { value: "lead_status", label: "Lead status", hint: "Lead stage follow-up messages" },
  { value: "email_enquiry", label: "Email enquiry", hint: "WhatsApp reply when an enquiry email arrives" },
];

const VALID = new Set([...TEMPLATE_SCREEN_TAGS, "all", DRAFT_TAG]);

export function parseTemplateAppUsages(value, defaultUsages = [DRAFT_TAG]) {
  let raw = [];
  if (Array.isArray(value)) {
    raw = value;
  } else if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) raw = [];
    else if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        raw = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        raw = [trimmed];
      }
    } else if (trimmed.includes(",")) {
      raw = trimmed.split(",");
    } else {
      raw = [trimmed];
    }
  }

  const tags = [...new Set(
    raw.map((v) => String(v || "").trim().toLowerCase()).filter((v) => VALID.has(v)),
  )];
  if (tags.includes("all")) return ["all"];
  const live = tags.filter((v) => TEMPLATE_SCREEN_TAGS.includes(v));
  if (live.length) return live;
  if (tags.includes(DRAFT_TAG)) return [DRAFT_TAG];
  const fallback = (defaultUsages || [DRAFT_TAG])
    .map((v) => String(v || "").trim().toLowerCase())
    .filter((v) => VALID.has(v));
  if (fallback.includes("all")) return ["all"];
  const fallbackLive = fallback.filter((v) => TEMPLATE_SCREEN_TAGS.includes(v));
  if (fallbackLive.length) return fallbackLive;
  return [DRAFT_TAG];
}

export function templateUsagesFromModel(template) {
  return parseTemplateAppUsages(
    template?.getAppUsages?.()
      || template?.attrs?.appUsages
      || template?.appUsages
      || template?.getAppUsage?.()
      || template?.attrs?.appUsage
      || template?.appUsage,
    [DRAFT_TAG],
  );
}

export function templateMatchesAppUsage(template, screen) {
  const want = String(screen || "").trim().toLowerCase();
  if (!want || want === "all") return true;
  const usages = templateUsagesFromModel(template);
  const hasAll = usages.includes("all");
  if (want === DRAFT_TAG) {
    return !hasAll && usages.length === 1 && usages[0] === DRAFT_TAG;
  }
  if (!hasAll && !usages.includes(want)) return false;
  const category = String(
    template?.getCategory?.() || template?.attrs?.category || "",
  ).toLowerCase();
  const isAuth = category.includes("auth") || category.includes("password") || category.includes("otp");
  if ((want === "inbox" || want === "quotation") && isAuth && hasAll) {
    return false;
  }
  return true;
}

export function isApprovedMetaTemplate(template) {
  const status = String(template?.getStatus?.() || template?.attrs?.status || "").toLowerCase();
  const metaId = template?.getTemplateId?.() || template?.attrs?.templateId;
  return status === "approved" && Boolean(metaId);
}

export function filterTemplatesForScreen(templates, screen) {
  return (templates || []).filter(
    (template) => isApprovedMetaTemplate(template) && templateMatchesAppUsage(template, screen),
  );
}

export function templateAppUsageLabels(value) {
  const usages = parseTemplateAppUsages(value, [DRAFT_TAG]);
  return usages.map((usage) => (
    TEMPLATE_APP_USAGE_OPTIONS.find((opt) => opt.value === usage)?.label || usage
  ));
}

/** @deprecated */
export function normalizeTemplateAppUsage(value) {
  const usages = parseTemplateAppUsages(value, [DRAFT_TAG]);
  if (usages.includes("all")) return "all";
  return usages[0] || DRAFT_TAG;
}

/** @deprecated */
export function templateAppUsageLabel(value) {
  return templateAppUsageLabels(value).join(", ");
}

export function toggleTemplateAppUsage(current, tag) {
  const usages = parseTemplateAppUsages(current, [DRAFT_TAG]);
  if (tag === "all") {
    return usages.includes("all") ? [DRAFT_TAG] : ["all"];
  }
  if (tag === DRAFT_TAG) {
    return [DRAFT_TAG];
  }
  const live = usages.filter((u) => TEMPLATE_SCREEN_TAGS.includes(u));
  const next = live.includes(tag)
    ? live.filter((u) => u !== tag)
    : [...live, tag];
  return next.length ? next : [DRAFT_TAG];
}
